import { useOrchestrationFabrics } from '@common/Fabric/hooks';
import { useOrchestrationMultiFabricQuery } from '@common/Fabric/OrchestrationFabric/api';
import { useOrchestrationHistoryApis } from '@common/Fabric/OrchestrationFabric/useOrchestrationHistoryApis';
import { useUserSQLProjects } from '@common/projectHooks/useUserSQLProjects';
import { ConcurrencyPolicy } from '@common/sqlIde/types/SensorSchedule';
import { renderColumnWithEllipsis } from '@common/utils/renderUtils';
import {
  AlertCircleIcon,
  AlertTriangleIcon,
  CheckCircleIcon,
  CircleIcon,
  FlipForwardIcon,
  HourglassAIcon,
  MinusCircleIcon,
  SearchSmIcon,
  SlashCircleAIcon,
  XCircleIcon
} from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { Button } from '@prophecy/ui-v3/Button';
import { Ellipsis } from '@prophecy/ui-v3/Ellipsis';
import { Input } from '@prophecy/ui-v3/Input';
import { Stack } from '@prophecy/ui-v3/Layout';
import { ColumnsType, Table } from '@prophecy/ui-v3/Table';
import { Tooltip } from '@prophecy/ui-v3/Tooltip';
import { Text } from '@prophecy/ui-v3/Typography';
import { formattedDateTime } from '@prophecy/utils/date';
import { useSearchParamState } from '@prophecy/utils/react/hooks';
import { capitalizeString, parseJSON } from '@prophecy/utils/string';
import { LastTriggerStatus, SqlPipelineRunType } from 'frontend/core/src/data/apis/types';
import { SlidingDetailsDialog } from 'frontend/core/src/generic-components/Logger/SlidingDetailsDialog';
import { Fabric } from 'frontend/core/src/redux/types';
import React, { SetStateAction, Dispatch, useMemo, useState } from 'react';
import styled from 'styled-components';

import { castArray } from '../../../data/util';
import TriggerBagdes from '../Components/TriggerBagdes';
import { SQLPipelineObservationLayout } from './Layout';
import { StyledMoreDetailsText, StyledSelect } from './styled';
import { OrchestrationSchedulePipelineInfo, SQLPipelineRunStatus } from './types';
import { renderPipelineLink, renderProjectLink, withEllipsis } from './utils';

const StyledInput = styled(Input)`
  width: 320px;
`;

const StyledTooltipContainer = styled(Stack)`
  max-width: 400px;
  max-height: 600px;
`;

function useFetchScheduledPipelines({
  fabricId,
  projectId,
  pipelineName
}: {
  fabricId?: string;
  projectId?: string | string[];
  pipelineName?: string;
}) {
  const { fabrics, fabricsMap, isLoading: isJWTLoading } = useOrchestrationFabrics();

  const { getScheduledPipelines } = useOrchestrationHistoryApis();

  const { data, isInitialLoading: isLoading } = useOrchestrationMultiFabricQuery(
    ['orchestration-scheduled-pipelines', fabricId, projectId, pipelineName],
    () =>
      getScheduledPipelines({
        projectIds: projectId ? castArray(projectId) : undefined,
        fabricIds: fabricId ? castArray(fabricId) : undefined,
        pipelineName
      })
  );

  const schedules = useMemo(() => {
    return castArray(data?.data.pipelines).map((pipeline) => {
      const sensorConfigDetails = parseJSON<OrchestrationSchedulePipelineInfo['sensorConfigDetails']>(
        pipeline.scheduleType,
        []
      );

      const scheduleInfo: OrchestrationSchedulePipelineInfo = {
        ...pipeline,
        sensorConfigDetails
      };

      return scheduleInfo;
    });
  }, [data]);

  return {
    schedules,
    isLoading: isLoading || isJWTLoading,
    fabrics,
    fabricsMap
  };
}

function LabelValueText({
  label,
  value,
  showEllipsis = false
}: {
  label: string;
  value: string;
  showEllipsis?: boolean;
}) {
  const TextWrapper = showEllipsis ? Ellipsis : React.Fragment;
  return (
    <Stack direction='horizontal' gap={theme.spaces.x6}>
      <Text level='sm' weight={theme.fontWeight.bold}>
        {label}:
      </Text>
      <TextWrapper>
        <Text level='sm'>{value}</Text>
      </TextWrapper>
    </Stack>
  );
}

const formatDateTime = (dateTime: string) => {
  return formattedDateTime(dateTime, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric'
  });
};

const getTooltip = (
  run: {
    id: string;
    createdAt: string;
    errorMessage?: string;
    runStatus: string;
  },
  onShowErrorInDetail: Dispatch<SetStateAction<string | null>>
) => {
  const errorMessage = run.errorMessage;
  return (
    <StyledTooltipContainer direction='vertical' gap={theme.spaces.x6}>
      <LabelValueText label='Run ID' value={run.id} />
      <LabelValueText label='Start Time' value={formatDateTime(run.createdAt)} />
      <LabelValueText label='Run Status' value={capitalizeString(run.runStatus.toLowerCase())} />
      {!!errorMessage ? (
        <Stack gap={theme.spaces.x12}>
          <LabelValueText label='Error' value={errorMessage} showEllipsis />
          <Button
            variant='plain'
            onClick={() => onShowErrorInDetail(errorMessage)}
            data-test-id='sql-pipeline-error-details-button'>
            <StyledMoreDetailsText level='sm' weight={theme.fontWeight.bold}>
              More Details
            </StyledMoreDetailsText>
          </Button>
        </Stack>
      ) : null}
    </StyledTooltipContainer>
  );
};

const getStatusIcon = (status: string) => {
  if (status === SQLPipelineRunStatus.SUCCEEDED) {
    return <CheckCircleIcon type='default' color={theme.colors.success600} />;
  } else if (status === SQLPipelineRunStatus.ERROR) {
    return <AlertCircleIcon type='default' color={theme.colors.error600} />;
  } else if (status === SQLPipelineRunStatus.WARNING) {
    return <AlertTriangleIcon type='default' color={theme.colors.warning600} />;
  } else if (status === SQLPipelineRunStatus.SKIPPED) {
    return <MinusCircleIcon type='default' color={theme.colors.gray500} />;
  } else if (status === SQLPipelineRunStatus.CANCELLED) {
    return <SlashCircleAIcon type='default' color={theme.colors.gray600} />;
  }
  // default to error when the status is not recognized
  return <AlertCircleIcon type='default' color={theme.colors.error600} />;
};

const CONCURRENCY_POLICY_ICONS: Record<ConcurrencyPolicy, React.ReactNode> = {
  [ConcurrencyPolicy.Queue]: <HourglassAIcon type='default' size='s' color={theme.colors.gray500} />,
  [ConcurrencyPolicy.Skip]: <FlipForwardIcon type='default' size='s' color={theme.colors.gray500} />,
  [ConcurrencyPolicy.Cancel]: <XCircleIcon type='default' size='s' color={theme.colors.gray500} />
};

const LAST_TRIGGER_STATUS_ICONS: Record<LastTriggerStatus, React.ReactNode> = {
  [LastTriggerStatus.Ran]: <CheckCircleIcon type='default' size='s' color={theme.colors.success600} />,
  [LastTriggerStatus.Skipped]: <MinusCircleIcon type='default' size='s' color={theme.colors.gray500} />
};

function IconLabel({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <Stack direction='horizontal' gap={theme.spaces.x6} alignY='center'>
      {icon}
      {label}
    </Stack>
  );
}

const getColumns = ({
  fabricsMap,
  projectsMap,
  onShowErrorInDetail
}: {
  fabricsMap: Map<string, Fabric>;
  projectsMap: Map<string, string>;
  onShowErrorInDetail: Dispatch<SetStateAction<string | null>>;
}): ColumnsType<OrchestrationSchedulePipelineInfo> => {
  return [
    {
      title: 'Pipeline',
      dataIndex: 'pipelineName',
      key: 'pipelineName',
      render: (_, record) =>
        renderPipelineLink({
          projectId: record.projectId,
          pipelineName: record.pipelineName,
          projectsMap,
          dataTestId: `sql-scheduled-pipelines-link-${record.pipelineName}-link`
        })
    },
    {
      title: 'Schedule Name',
      dataIndex: 'name',
      key: 'name',
      render: renderColumnWithEllipsis
    },
    {
      title: 'Fabric',
      render: (_, record) => {
        const fabric = fabricsMap.get(record.fabricId);
        return withEllipsis(fabric?.name || record.fabricId);
      },
      key: 'fabric'
    },
    {
      title: 'Project',
      render: (_, record) =>
        renderProjectLink({
          projectId: record.projectId,
          projectsMap,
          dataTestId: `sql-scheduled-pipelines-project-${record.projectId}-link`
        }),
      key: 'projectId'
    },
    {
      title: 'Triggers',
      width: '20%',
      render: (_, record) => (
        <TriggerBagdes
          cron={record.cron}
          sensorConfigDetails={record.sensorConfigDetails}
          runType={SqlPipelineRunType.Scheduled}
        />
      ),
      key: 'runFrequency'
    },
    {
      title: 'Concurrency Policy',
      key: 'concurrencyPolicy',
      render: (_, record) => {
        const policy = record.concurrencyPolicy || ConcurrencyPolicy.Queue;
        return <IconLabel icon={CONCURRENCY_POLICY_ICONS[policy]} label={capitalizeString(policy)} />;
      }
    },
    {
      title: 'Last Trigger Status',
      key: 'lastTriggerStatus',
      render: (_, record) =>
        record.lastTriggerStatus ? (
          <IconLabel
            icon={LAST_TRIGGER_STATUS_ICONS[record.lastTriggerStatus]}
            label={capitalizeString(record.lastTriggerStatus)}
          />
        ) : (
          ''
        )
    },
    {
      title: 'Last Trigger Time',
      key: 'lastTriggerTime',
      width: '180px',
      render: (_, record) => (record.lastTriggerTime ? formatDateTime(record.lastTriggerTime) : '')
    },
    {
      title: 'Last 5 runs',
      width: '170px',
      render: (_, record) => {
        const runs = record.runs
          .filter(
            (run) => run.runStatus !== SQLPipelineRunStatus.RUNNING && run.runStatus !== SQLPipelineRunStatus.CANCELLING
          )
          .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
          .map((run) => (
            <Tooltip key={run.id} title={getTooltip(run, onShowErrorInDetail)}>
              <Stack>{getStatusIcon(run.runStatus)}</Stack>
            </Tooltip>
          ));

        if (runs.length < 5) {
          for (let i = runs.length + 1; i <= 5; i++) {
            runs.push(<CircleIcon key={`empty-${i - 1}`} type='default' color={theme.colors.gray200} />);
          }
        }

        return (
          <Stack direction='horizontal' gap={theme.spaces.x6}>
            {runs}
          </Stack>
        );
      },
      key: 'runs'
    }
  ] as ColumnsType<OrchestrationSchedulePipelineInfo>;
};

export function ScheduledPipelines() {
  const [fabricId, setFabricId] = useSearchParamState('spFabricId');
  const [projectId, setProjectId] = useSearchParamState('spProjectId');
  const [pipelineName, setPipelineName] = useSearchParamState('spPipelineName');
  const { projectsMap, isLoading: isProjectsLoading, projects } = useUserSQLProjects();
  const {
    schedules,
    isLoading: isSchedulesLoading,
    fabrics,
    fabricsMap
  } = useFetchScheduledPipelines({
    fabricId,
    projectId: projectId || projects.map((project) => project._id),
    pipelineName: pipelineName || undefined
  });

  const [errorMessageToShow, setErrorMessageToShow] = useState<string | null>(null);
  const columns = useMemo(
    () => getColumns({ fabricsMap, projectsMap, onShowErrorInDetail: setErrorMessageToShow }),
    [fabricsMap, projectsMap]
  );

  return (
    <SQLPipelineObservationLayout title='Scheduled Pipelines' count={schedules.length}>
      <Stack direction='horizontal' gap={theme.spaces.x20} alignY='end'>
        <StyledInput
          placeholder='Search pipeline'
          icon={<SearchSmIcon type='default' />}
          value={pipelineName}
          onChange={(value) => setPipelineName(value || undefined)}
          iconPlacement='left'
          data-test-id='sql-scheduled-pipelines-search-input'
        />
        <StyledSelect
          label='Fabric'
          allowClear
          placeholder='Select Fabric'
          data-test-id='sql-scheduled-pipelines-fabric-select'
          options={fabrics.map((fabric) => ({
            label: fabric.name,
            value: fabric.id
          }))}
          value={fabricId}
          onChange={setFabricId}
        />
        <StyledSelect
          label='Project'
          allowClear
          placeholder='Select Project'
          options={projects.map((project) => ({
            label: project.name,
            value: project._id
          }))}
          value={projectId}
          onChange={setProjectId}
          data-test-id='sql-scheduled-pipelines-project-select'
        />
      </Stack>
      <Table
        loading={isSchedulesLoading || isProjectsLoading}
        virtualize
        placeholderRows={50}
        height='100%'
        width='100%'
        rowKey='id'
        fixedHeader
        data-test-id='sql-scheduled-pipelines-table'
        columns={columns}
        dataSource={schedules}
      />
      {errorMessageToShow && (
        <SlidingDetailsDialog
          title='Error Details'
          onClose={() => setErrorMessageToShow(null)}
          message={errorMessageToShow}
          type='error'
        />
      )}
    </SQLPipelineObservationLayout>
  );
}
